import { PublicClientApplication } from '@azure/msal-browser'
import { appInsights } from 'src/AppInsights'
import { msalInstance } from 'src/auth/msal'
import {
  setBidAnalysisFilters,
  setCurrencySymbol,
  setDefaultOwnerFilter,
} from 'src/redux/slices/bidAnalysisFilters.slice'
import { setRole } from 'src/redux/slices/Config/userProfile.slice'
import { currencySymbols } from 'src/utils/Functions/currency/getCurrencySymbol'
// var token = `${localStorage.getItem("accesstoken")}`;

const publicClientApplication = new PublicClientApplication({
  auth: {
    clientId: import.meta.env.VITE_AUTH_APP_ID,
    redirectUri: import.meta.env.VITE_AUTH_REDIRECT_URI,
    authority: import.meta.env.VITE_AUTHORITY,
  },
  cache: {
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: false,
    secureCookies: false,
  },
})

// AZURE AD login
export const azureADLogin = async () => {
  try {
    await publicClientApplication.loginPopup({
      scopes: import.meta.env.VITE_SCOPES,
      prompt: 'select_account',
    })
    return {
      status: 200,
      data: localStorage,
    }
  } catch (error) {
    return error.message
  }
}

export default function emptyCache() {
  if ('caches' in window) {
    caches.keys().then((names) => {
      // Delete all the cache files
      names.forEach((name) => {
        caches.delete(name)
      })
    })

    // Makes sure the page reloads. Changes are only visible after you refresh.
    // window.location.reload(true)
  }
}

//Logout
export const doLogout = async () => {
  try {
    emptyCache()
    const account = msalInstance.getActiveAccount()
    clearAllStorages()
    appInsights.unload()
    window.onbeforeunload = null
    const redirectParam = window.location.hash
      ? '&redirect=' + encodeURIComponent(window.location.hash)
      : ''
    window.location.href = '/?sessionExpired=true' + redirectParam
    return {
      status: 200,
      data: 'Logged Out',
    }
  } catch (error) {
    return error.message
  }
}

export const fetchUser = async (token, dispatch) => {
  try {
    const response = await fetch(`${import.meta.env.VITE_API_URL}/v1/common/get-user`, {
      headers: { Authorization: 'Bearer ' + token, 'Content-Type': 'application/json' },
    })
    if (!response.ok) {
      throw new Error('Something went wrong!')
    }
    const dBdata = await response.json()

    if (dBdata?.user_roles?.length > 0) {
      dispatch(setRole(dBdata.user_roles))
    }
    // Set currency symbol in bidAnalysisFilter slice if currency_preference exists
    if (dispatch && dBdata?.currency_preference) {
      dispatch(setCurrencySymbol(currencySymbols[dBdata.currency_preference] || '$'))
      dispatch(setBidAnalysisFilters({ targetCurrency: dBdata.currency_preference }))
    }
    if (dispatch && dBdata) {
      const defaultOwnerData = {
        label: dBdata.name || dBdata.preferred_username,
        value: dBdata.preferred_username || dBdata.email,
        email: dBdata.preferred_username || dBdata.email,
        id: dBdata.id || dBdata.oid,
      }
      dispatch(setDefaultOwnerFilter(defaultOwnerData))
    }
    return {
      status: response.status,
      data: dBdata,
    }
  } catch (error) {
    // navigateHome()
    return error.message
  }
}

export const fetchUserDefaultRoute = async (token) => {
  try {
    const response = await fetch(
      `${import.meta.env.VITE_API_URL}/v1/common/get-user-with-default-route`,
      {
        headers: { Authorization: 'Bearer ' + token },
      },
    )
    if (!response.ok) {
      throw new Error('Something went wrong!')
    }
    const dBdata = await response.json()
    return {
      status: response.status,
      data: dBdata,
    }
  } catch (error) {
    // navigateHome()
    return error.message
  }
}

export const fetchAppConfig = async (token) => {
  try {
    const response = await fetch(`${import.meta.env.VITE_API_URL}/v1/app-config/get-config`, {
      headers: { Authorization: 'Bearer ' + token },
    })
    if (!response.ok) {
      if (response.status === 403) {
        console.log(response)
        throw Error(response.statusText)
      }
      throw new Error('Something went wrong!')
    }
    const dBdata = await response.json()
    return {
      status: response.status,
      data: dBdata,
    }
  } catch (error) {
    if (error.message === 'Forbidden') {
      // alert('Session Expired!. Please Login Again.')
      doLogout()
      navigateHome()
      console.log(error)
    }
    return error.message
  }
}

export const navigateHome = () => {
  clearAllStorages()
  window.location.href = '/#/'
}

export const clearAllStorages = () => {
  localStorage.clear()
  sessionStorage.clear()
  document.cookie = ''
}

export async function getUserDetailsFromAccessToken(token, dispatch) {
  let response = await fetchUser(token, dispatch)
  if (response.status === 200) {
    return response.data
  }
}

async function getConfigDetails() {
  try {
    const response = await fetch(`${import.meta.env.VITE_API_URL}/v1/configurations/app-settings`, {
      headers: { Authorization: 'Bearer ' + localStorage.getItem('accesstoken') },
    })
    const data = await response.json()
    return data
  } catch (err) {
    console.log('err: ', err)
  }
}

export async function setTokensInLocalStorage(dispatch) {
  try {
    // 1. Extract tokens from sessionStorage
    const values = Object.values(sessionStorage)
      .map((store) => {
        try {
          return JSON.parse(store)
        } catch {
          return null
        }
      })
      .filter((parsed) => parsed?.credentialType && parsed?.secret)

    values.forEach((entry) => {
      if (entry.credentialType === 'IdToken') {
        localStorage.setItem('accesstoken', entry.secret)
      } else if (entry.credentialType === 'RefreshToken') {
        localStorage.setItem('refreshtoken', entry.secret)
      }
    })

    // 2. Fetch user
    const accessToken = localStorage.getItem('accesstoken')
    const user = await getUserDetailsFromAccessToken(accessToken, dispatch)

    if (user) localStorage.setItem('uname', JSON.stringify(user))

    // 3. Fetch config once (only if needed)
    let configMap = {}

    const getConfigValue = async (key) => {
      if (!Object.keys(configMap).length) {
        const response = await getConfigDetails()
        configMap = (response?.data || []).reduce((acc, item) => {
          acc[item.name] = item.value
          return acc
        }, {})
      }
      return configMap[key]
    }

    // 4. Generic setter (REUSABLE)
    const setPreference = async (storageKey, userKey, configKey) => {
      if (user?.[userKey]) {
        localStorage.setItem(storageKey, user[userKey])
      } else {
        const value = await getConfigValue(configKey)
        if (value) localStorage.setItem(storageKey, value)
      }
    }

    // 5. Set all preferences
    await setPreference('default_currency', 'currency_preference', 'platform.currency.default_currency')
    await setPreference('theme', 'theme_preference', 'platform.theme.default_theme')
    await setPreference(
      'top_down_creation_preference',
      'top_down_creation_preference',
      'platform.creation.top_down_preference',
    )
    await setPreference('instance_name', 'instance_name', 'platform.instance.instance_name')

    await setPreference('gto_sidebar', 'gto_sidebar', 'platform.navigation.use_legacy_sidebar')
    await setPreference('is_created_for', 'is_created_for', 'is_created_for')
    // if (user) {
    //   localStorage.setItem('gto_sidebar', user.gto_sidebar)
    // } else {
    //   localStorage.setItem('gto_sidebar', true)
    // }
  } catch (error) {
    throw new Error(error)
  }
}
